import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Textfield } from '@bi-digital/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type AuthCatalogEmailForm, authCatalogEmailSchema } from '../../types';

import { getIdentifyUserAndSendEmail } from '../../api';
import { Result } from '../../components/result/result';
import styles from './signUp.module.css';

export interface SignUpProps {
    title: string;
    helpText: string;
    emailInputHeading: string;
    emailInputLabel: string;
    buttonText: string;
    contactText: string;
    confirmationText: string;
    confirmationText2: string;
    confirmationText3: string;
    serverErrorText: string;
}
export const SignUp = ({
    title,
    helpText,
    emailInputHeading,
    emailInputLabel,
    buttonText,
    contactText,
    confirmationText,
    confirmationText2,
    confirmationText3,
    serverErrorText,
}: SignUpProps) => {
    const [wizardStep, setWizardStep] = useState<'form' | 'confirm' | 'error'>('form');

    const { t } = useTranslation(['iam', 'common']);

    const { handleSubmit, formState, register } = useForm<AuthCatalogEmailForm>({
        resolver: zodResolver(authCatalogEmailSchema),
    });

    const submitForm = handleSubmit(async ({ email }) => {
        try {
            await getIdentifyUserAndSendEmail(email);
            setWizardStep('confirm');
        } catch {
            setWizardStep('error');
        }
    });

    if (wizardStep === 'form') {
        return (
            <div className={styles.signUp}>
                <h1>{title}</h1>
                {helpText && <p className={styles.helpText}>{helpText}</p>}
                <form className={styles.form} onSubmit={submitForm}>
                    {emailInputHeading && <h2 className={styles.emailHeading}>{emailInputHeading}</h2>}
                    <Textfield
                        autoComplete='email'
                        className={styles.input}
                        label={emailInputLabel}
                        required
                        error={t(formState.errors.email?.message ?? '')}
                        {...register('email')}
                    />
                    <Button
                        className={styles.submitButton}
                        disabled={formState.isSubmitting}
                        submitting={formState.isSubmitting}
                        type='submit'
                    >
                        {buttonText}
                    </Button>
                </form>
                {contactText && <p className={styles.helpText}>{contactText}</p>}
            </div>
        );
    }
    if (wizardStep === 'confirm') {
        return (
            <div className={styles.confirm} role='status'>
                <h1>{confirmationText}</h1>
                <p>{confirmationText2}</p>
                <p>{confirmationText3}</p>
            </div>
        );
    }
    return <Result id='error-iam' message={serverErrorText} />;
};
